import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import useAuthStore from '@/store/authStore';
import Sidebar, { SidebarNav } from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import { ErrorBoundary } from '@/lib/errors';
import { ROUTES } from '@/config/routes';

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { tenant, user, clearAuth } = useAuthStore();

  const handleLogout = () => {
    clearAuth();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="h-screen flex bg-[#09090b] text-zinc-100 overflow-hidden">
      <Sidebar tenant={tenant} user={user} onLogout={handleLogout} />

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-zinc-950 border-r border-zinc-800 z-50 flex flex-col lg:hidden"
            >
              <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-emerald-950" />
                  </div>
                  <span className="font-bold text-lg tracking-tight font-[Manrope]">Agentick</span>
                </div>
                <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-zinc-800">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <ScrollArea className="flex-1 py-4">
                <SidebarNav onItemClick={() => setMobileOpen(false)} />
              </ScrollArea>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header onMobileMenuToggle={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
